import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/States";

export function BillingSuccess() {
  const [params] = useSearchParams();
  const { refreshWorkspace } = useAuth();
  const [state, setState] = useState("checking");
  const sid = params.get("session_id");

  useEffect(() => {
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") { await refreshWorkspace().catch(() => {}); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (++tries < 8) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    if (sid) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [sid, refreshWorkspace]);

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="rise max-w-md space-y-4 text-center" data-testid="billing-success">
        {state === "checking" && <><Spinner /><p className="text-sm text-muted-foreground">Confirming your subscription…</p></>}
        {state === "paid" && <><CheckCircle2 className="mx-auto h-12 w-12 text-primary" /><h1 className="font-serif text-4xl">You're all set</h1><p className="text-sm text-muted-foreground">Your subscription is active. Thanks for building with us.</p></>}
        {state === "timeout" && <><h1 className="section-title">Still processing</h1><p className="text-sm text-muted-foreground">Stripe is finishing up. Your plan will update on the Billing page within a minute.</p></>}
        {state === "failed" && <><XCircle className="mx-auto h-12 w-12 text-destructive" /><h1 className="section-title">Payment didn't complete</h1><p className="text-sm text-muted-foreground">No charge was made. You can try again from Billing.</p></>}
        {state !== "checking" && <Button asChild className="rounded-full" data-testid="billing-success-continue"><Link to="/billing">Go to Billing</Link></Button>}
      </div>
    </div>
  );
}

export function BillingCancel() {
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="rise max-w-md space-y-4 text-center" data-testid="billing-cancel">
        <h1 className="font-serif text-4xl">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground">Nothing was charged. Your trial and data are unchanged.</p>
        <Button asChild className="rounded-full" data-testid="billing-cancel-back"><Link to="/billing">Back to Billing</Link></Button>
      </div>
    </div>
  );
}
